import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
    Box,
    Checkbox,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import { GIF_MAX_DURATION } from '../constants';
import SliderNumeric from './sliderNumeric';
import SliderDualNumeric from './sliderDualNumeric';
import SliderSingle from './sliderSingle';
import { translate } from '../constants';


const CustomModal = ({
    open,
    onClose,
    color,
    title,
    type,  // 'slider' or 'search' or 'views' or 'create' or 'warning'
    // Slider specific props
    value,
    onChange,
    onChangeCommitted,
    min,
    max,
    precision = false,
    step = 1,
    unit = '',
    isSingleValue = false, // New prop to indicate single value slider
    defaultValue = 250, // Default value for reset button
    // Search specific props
    onSearchChange,
    searchValue,
    store,
    // Views specific props
    options = [],
    // Create specific props
    selectedOptions = [],
    onOptionChange,
    // Warning specific props
    message = '',
    onConfirm, // New prop for confirmation callback
    // Media creation props
    onCreateMedia,
}) => {
    const theme = useTheme();
    const [initialRange] = useState({ min, max });
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const wide = (initialRange.max - initialRange.min);


    const handleMouseDown = (e) => {
        if (e.target.closest('.modal-close-btn')) return; // Ignore if clicking close button
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStart]);

    // Handler for the clear button
    const handleClearSearch = () => {
        // Mimic the event structure expected by onSearchChange
        onSearchChange({ target: { value: '' } });
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="custom-modal"
            BackdropProps={{
                sx: { backgroundColor: 'transparent' },
                onClick: (e) => {
                    if (e.target === e.currentTarget) onClose();
                }
            }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            slotProps={{
                backdrop: {
                    timeout: 0,
                }
            }}
            style={{ pointerEvents: 'none' }}
        >
            <Paper
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
                    width: {
                        xs: type === 'search' ? '90vw' :
                            type === 'slider' ? '90vw' :
                                type === 'views' ? '80vw' :
                                    type === 'create' ? '90vw' :
                                        type === 'warning' ? '90vw' : '70vw',
                        sm: type === 'search' ? 400 :
                            type === 'slider' ? Math.min(Math.max(400, Math.max(600, wide)), '80vw') :
                                type === 'views' ? 300 :
                                    type === 'create' ? 400 :
                                        type === 'warning' ? 400 : 200,
                        md: type === 'search' ? 400 :
                            type === 'slider' ? Math.max(400, Math.max(600, wide)) :
                                type === 'views' ? 300 :
                                    type === 'create' ? 400 :
                                        type === 'warning' ? 400 : 200
                    },
                    maxWidth: '95vw',
                    minHeight: type === 'slider' && precision ? 280 : 'auto',
                    bgcolor: 'transparent',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(40, 40, 40, 0.5)'
                        : 'rgba(240, 240, 240, 0.5)',
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${color}30`,
                    overflow: 'hidden',
                    pointerEvents: 'auto',
                    userSelect: 'none'
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        p: { xs: 1.5, sm: 2 },
                    }}
                >
                    <Box
                        onMouseDown={handleMouseDown}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '40px',
                            cursor: 'move',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.03)'
                                : 'rgba(0, 0, 0, 0.03)',
                            borderTopLeftRadius: 'inherit',
                            borderTopRightRadius: 'inherit',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.05)',
                            }
                        }}
                    >
                        {type !== 'warning' && (
                            <Typography variant="subtitle1" sx={{
                                color: color,
                                fontWeight: 500,
                                textAlign: 'center',
                                opacity: 0.9,
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                px: { xs: 1, sm: 0 }
                            }}>
                                {title}
                            </Typography>
                        )}
                    </Box>

                    <IconButton
                        className="modal-close-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        size="small"
                        sx={{
                            position: 'absolute',
                            right: 4,
                            top: 8,
                            color: theme.palette.text.secondary,
                            opacity: 0.6,
                            zIndex: 1,
                            '&:hover': {
                                color: color,
                                opacity: 1,
                                backgroundColor: `${color}10`
                            }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    <Stack spacing={2} sx={{ mt: 4, pt: 1 }}>
                        <Box sx={{ px: { xs: 1, sm: 2 } }}>
                            {type === 'warning' ? (
                                <>
                                    <Alert
                                        severity="warning"
                                        sx={{
                                            alignItems: 'center',
                                            '& .MuiAlert-message': {
                                                width: '100%',
                                                textAlign: 'center'
                                            }
                                        }}
                                    >
                                        {message}
                                    </Alert>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Tooltip title="Confirm">
                                            <Button
                                                onClick={() => onConfirm(true)}
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                sx={{
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    px: { xs: 2, sm: 3 }
                                                }}
                                            >
                                                Yes
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Cancel">
                                            <Button
                                                onClick={() => onConfirm(false)}
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                sx={{
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    px: { xs: 2, sm: 3 },
                                                    ml: 1
                                                }}
                                            >
                                                No
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </>
                            ) : type === 'slider' ? (
                                <>
                                    {isSingleValue ? (
                                        <>
                                            <SliderSingle
                                                min={min}
                                                max={max}
                                                label={title.replace(/\s*\([^)]*\)/, '')} // Remove unit from title for label
                                                color={color === theme.palette.success.main ? 'success' :
                                                    color === theme.palette.warning.main ? 'warning' :
                                                        color === theme.palette.primary.main ? 'primary' :
                                                            'primary'}
                                                callback={(newValue) => {
                                                    onChange(null, newValue);
                                                }}
                                                reset={true}
                                                initialValue={Array.isArray(value) ? value[0] : value}
                                                step={step}
                                                unit={unit}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                <Tooltip title="Reset to default">
                                                    <IconButton
                                                        onClick={() => onChange(null, defaultValue)} // Responsive default video width
                                                        size="small"
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                        }}
                                                    >
                                                        <RestartAltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </>
                                    ) : precision ? (
                                        <>
                                            <SliderDualNumeric
                                                textLen={[initialRange.min, initialRange.max]}
                                                label=""
                                                color={color === theme.palette.success.main ? 'success' :
                                                    color === theme.palette.warning.main ? 'warning' :
                                                        'primary'}
                                                callback={(newValues) => {
                                                    onChange(null, newValues);
                                                }}
                                                reset={true}
                                                initialValue={value}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                <Tooltip title="Reset to initial value">
                                                    <IconButton
                                                        onClick={() => onChange(null, [initialRange.min, initialRange.max])}
                                                        size="small"
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                        }}
                                                    >
                                                        <RestartAltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <SliderNumeric
                                                textLen={[initialRange.min, initialRange.max]}
                                                label=""
                                                color={color === theme.palette.success.main ? 'success' :
                                                    color === theme.palette.warning.main ? 'warning' :
                                                        'primary'}
                                                callback={(newValues) => {
                                                    onChange(null, newValues);
                                                }}
                                                reset={true}
                                                initialValue={value}
                                            />
                                            <Typography variant="caption" sx={{
                                                color: theme.palette.text.secondary,
                                                textAlign: 'center',
                                                display: 'block',
                                                mb: 1,
                                                fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                            }}>
                                            </Typography>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                <Tooltip title="Reset to full range">
                                                    <IconButton
                                                        onClick={() => onChange(null, [initialRange.min, initialRange.max])}
                                                        size="small"
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                        }}
                                                    >
                                                        <RestartAltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </>
                                    )}
                                </>
                            ) : type === 'search' ? (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            value={searchValue || ''}
                                            onChange={onSearchChange}
                                            placeholder="Search..."
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: `${color}40`,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: `${color}80`,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: color,
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                endAdornment: searchValue ? (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Clear search">
                                                            <IconButton
                                                                edge="end"
                                                                onClick={handleClearSearch}
                                                                size="small"
                                                                sx={{ color: `${color}80` }}
                                                            >
                                                                <RestartAltIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                        />
                                    </Box>
                                </>
                            ) : type === 'views' ? (
                                <FormControl>
                                    <RadioGroup
                                        value={value}
                                        onChange={onChange}
                                    >
                                        {options.map((option) => (
                                            <FormControlLabel
                                                key={option}
                                                value={option}
                                                control={<Radio size="small" sx={{ color: color }} />}
                                                label={<Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{translate(option)}</Typography>}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            ) : type === 'create' ? (
                                <FormControl sx={{ width: '100%' }}>
                                    <Stack spacing={2}>
                                        {options.map((option) => {
                                            // Check if option is an array with a boolean flag
                                            const optionName = Array.isArray(option) ? option[0] : option;
                                            const isEnabled = Array.isArray(option) ? option[1] : true;
                                            // Create tooltip content for disabled GIF option
                                            const tooltipContent = !isEnabled && optionName === 'Create a GIF'
                                                ? `GIF creation is limited to ${GIF_MAX_DURATION / 1000} seconds`
                                                : '';

                                            return (
                                                <Tooltip
                                                    key={optionName}
                                                    title={tooltipContent}
                                                    placement="top"
                                                    arrow
                                                    disableInteractive
                                                >
                                                    <FormControlLabel
                                                        sx={{
                                                            mx: 0,
                                                            '& .MuiFormControlLabel-label': {
                                                                flex: 1
                                                            }
                                                        }}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedOptions.includes(optionName)}
                                                                onChange={(e) => onOptionChange(optionName, e.target.checked)}
                                                                size="small"
                                                                sx={{ color: color }}
                                                                disabled={!isEnabled}
                                                            />
                                                        }
                                                        label={<Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{optionName}</Typography>}
                                                    />
                                                </Tooltip>
                                            );
                                        })}

                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                            <Tooltip title="Create selected items">
                                                <Button
                                                    onClick={() => {
                                                        onCreateMedia(selectedOptions);
                                                        onClose();
                                                    }}
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                    sx={{
                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                        px: { xs: 2, sm: 3 }
                                                    }}
                                                >
                                                    OK
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    </Stack>
                                </FormControl>
                            ) : null}
                        </Box>
                    </Stack>
                </Box>
            </Paper>
        </Modal>
    );
};


export default CustomModal;
